export type EvidenceStatus = 'observed' | 'no_source_rows' | 'unavailable';
export type ReviewWindow = 'last_7d' | 'last_30d' | 'custom';
export interface PerformanceDateRange {
  start_date: string;
  end_date: string;
}

export type GroupBy = 'all' | 'country' | 'contact';

export interface CoveredAmount {
  source_rows?: number;
  populated_rows?: number;
  value: number | null;
  observed_subtotal: number | null;
  contributing_hotels: number;
  total_hotels: number;
  status: 'observed_all_selected_hotels' | 'partial' | 'unavailable';
}

export interface WindowMetrics {
  start_date: string;
  end_date: string;
  lpc_margin_ar_aud: CoveredAmount;
  lpp_margin_ar_aud: CoveredAmount;
  lpc_ttv_ar_aud: CoveredAmount;
  lpp_ttv_ar_aud: CoveredAmount;
  margin_change_7d_aud: {
    value: number | null;
    matched_hotels: number;
    total_hotels: number;
    status: string;
  };
}

export interface HealthProperty {
  property_id: string;
  property_name: string;
  country: string | null;
  market_manager: string | null;
  evidence: Record<'commercial' | 'impressions' | 'parity', EvidenceStatus>;
  windows?: Partial<Record<ReviewWindow, WindowMetrics>>;
}

export interface SourceWarning {
  id: string;
  status: 'unresolved';
  severity: 'warning';
  property_id: string;
  event_date: string;
  title: string;
  detail: string;
}

export interface HealthSnapshot {
  source_warnings?: SourceWarning[];
  snapshot_id: string;
  report_date: string;
  generated_at: string;
  source_completion: 'unverified';
  contract_version: string;
  pilot_properties: number;
  eligible_properties: number;
  parity_neutral_tolerance: number;
  stale: boolean;
  unavailable: Record<string, string>;
  properties: HealthProperty[];
  windows?: Partial<Record<ReviewWindow, WindowMetrics>>;
  attribution_model?: string;
  grouping_unavailable?: Record<string, string>;
}

export interface CommercialRow {
  report_date: string;
  observation_date?: string;
  product_bk: string;
  source_rows: number;
  margin_observed_rows: number;
  ttv_observed_rows: number;
  margin_ar_aud: number | null;
  ttv_ar_aud: number | null;
}

export interface ImpressionRow {
  report_date: string;
  channel: 'LPC' | 'LPP';
  source_rows: number;
  impression_observed_rows: number;
  impressions: number | null;
}

export interface ParityExample {
  offer_bk: string;
  rate_id: string;
  check_in_date: string;
  check_out_date: string;
  room_name: string;
  board_code: string;
  currency: string;
  le_price_nightly: number;
  competitor_price_nightly: number;
  competitor: string;
  competitor_name: string;
  gap_pct: number;
}

export interface ParityRow {
  report_date: string;
  tracked_competitor_checks: number;
  losses: number;
  wins: number;
  price_neutral: number;
  parity_score: number | null;
  recheck_examples: ParityExample[];
}

export interface PropertyEvidence {
  metadata: Omit<HealthSnapshot, 'properties'>;
  property_id: string;
  stale: boolean;
  evidence: {
    scope: HealthProperty & { live_lpc_offer_bk: string; live_lpp_offer_bk: string };
    commercial: CommercialRow[];
    impressions: ImpressionRow[];
    parity: ParityRow[];
    commercial_history?: CommercialRow[];
  };
}

export function isLpcOfferHealthEnabled(): boolean {
  return false;
}
